import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useCreateTemplate, useUpdateTemplate } from '@/hooks/useCampaignTemplates';
import { CampaignTemplate } from '@/services/campaignTemplateService';
import { replaceMergeFields, getExampleVariables, extractVariablesFromTemplate } from '@/lib/campaignMergeFields';
import MergeFieldPicker from './MergeFieldPicker';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  template?: CampaignTemplate | null;
}

const CampaignTemplateEditor: React.FC<Props> = ({ open, onOpenChange, companyId, template }) => {
  const isEdit = !!template?.id;
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();

  const [name, setName] = useState('');
  const [type, setType] = useState('custom');
  const [category, setCategory] = useState('');
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [templateMode, setTemplateMode] = useState('guide');

  useEffect(() => {
    if (template) {
      setName(template.name);
      setType(template.template_type);
      setCategory(template.category || '');
      setSubjectTemplate(template.subject_template);
      setBodyTemplate(template.body_template);
      setIsActive(template.is_active);
      setTemplateMode((template as any).template_mode || 'guide');
    } else {
      setName(''); setType('custom'); setCategory('ai'); setSubjectTemplate(''); setBodyTemplate(''); setIsActive(true); setTemplateMode('guide');
    }
  }, [template, open]);

  const handleSave = async () => {
    const variables = extractVariablesFromTemplate(subjectTemplate + ' ' + bodyTemplate);
    const payload = {
      company_id: companyId,
      name,
      template_type: type,
      category: category || null,
      subject_template: subjectTemplate,
      body_template: bodyTemplate,
      variables,
      is_active: isActive,
      template_mode: templateMode,
      created_by: (await supabase.auth.getUser()).data.user?.id,
    };

    if (isEdit && template) {
      await updateMutation.mutateAsync({ id: template.id, updates: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    onOpenChange(false);
  };

  const exampleVars = getExampleVariables();
  const renderedSubject = replaceMergeFields(subjectTemplate, exampleVars);
  const renderedBody = replaceMergeFields(bodyTemplate, exampleVars);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Template' : 'Create Template'}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Template name" />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['marketing', 'notification', 'welcome', 'follow_up', 'custom'].map(t => (
                  <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category || 'ai'} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ai">AI Generated — AI writes unique content per recipient</SelectItem>
                <SelectItem value="merge-fields">Merge Fields — Uses {'{{placeholders}}'} filled with lead data</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>Active</Label>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Template Mode</Label>
          <Select value={templateMode} onValueChange={setTemplateMode}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="guide">Guide — AI generates a full email using this as guidance</SelectItem>
              <SelectItem value="strict">Strict — AI follows these instructions exactly, no extras added</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {templateMode === 'guide' 
              ? 'The AI will use its full personalisation rules (greetings, CTA, signature) and treat the template body as supplementary guidance.'
              : 'The AI will follow your template instructions exactly. It will NOT add greetings, signatures, CTAs, or contact details unless your template asks for them.'}
          </p>
        </div>

        <div className="space-y-2">
          <Label>Subject Template *</Label>
          <Input value={subjectTemplate} onChange={e => setSubjectTemplate(e.target.value)} placeholder="Hello {{lead_name}}..." />
        </div>

        <Tabs defaultValue="editor">
          <TabsList>
            <TabsTrigger value="editor">HTML Template</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="editor" className="space-y-2 mt-2">
            <RichTextEditor value={bodyTemplate} onChange={setBodyTemplate} placeholder="Write your template..." />
            <MergeFieldPicker onInsert={(field) => setBodyTemplate(prev => prev + `{{${field}}}`)} />
          </TabsContent>
          <TabsContent value="preview" className="mt-2">
            <div className="border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-2">Subject: <strong>{renderedSubject}</strong></div>
              <div dangerouslySetInnerHTML={{ __html: renderedBody }} />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name || !subjectTemplate || !bodyTemplate || createMutation.isPending || updateMutation.isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignTemplateEditor;
